import { TaskValue } from 'lib/actions/task'
import { SystemAnyAccessEntitlementID } from 'lib/constants/system'
import { IntlShape } from 'react-intl'

export const getTaskTitleIntl = (task: TaskValue, intl: IntlShape): string => {
    if (!task.type?.taskType) {
        return intl.formatMessage({
            id: 'components_task_dialog_unknown_task',
            defaultMessage: 'Unknown task type',
        })
    }

    switch (task.type.taskType.oneofKind) {
        case 'grant':
            return getGrantTaskTitleIntl(task, intl)
        case 'revoke':
            return getRevokeTaskTitleIntl(task, intl)
        case 'certify':
            return getCertifyTaskTitleIntl(task, intl)
        case 'action':
            return getActionTaskTitleIntl(task, intl)
        case 'offboarding':
            return intl.formatMessage({
                id: 'components_task_dialog_offboarding_task',
                defaultMessage: 'Offboarding task',
            })
        default:
            return intl.formatMessage({
                id: 'components_task_dialog_unknown_task',
                defaultMessage: 'Unknown task type',
            })
    }
}

const getGrantTaskTitleIntl = (task: TaskValue, intl: IntlShape): string => {
    const userName = task.user?.displayName || task.identityUser?.displayName || task.appUser?.displayName
    const isExtension = task.type?.taskType.oneofKind === 'grant' && task.type.taskType.grant.source?.isExtension

    if (task.appEntitlements?.[0]?.id === SystemAnyAccessEntitlementID) {
        if (isExtension) {
            return intl.formatMessage(
                {
                    id: 'components_task_dialog_grant_task_extension_grant_app_access',
                    defaultMessage: 'Extension: {subject} needs application access to {app}',
                },
                {
                    subject: userName,
                    app: task.app?.displayName,
                },
            )
        }
        return intl.formatMessage(
            {
                id: 'components_task_dialog_grant_task_grant_app_access',
                defaultMessage: '{subject} needs application access to {app}',
            },
            {
                subject: userName,
                app: task.app?.displayName,
            },
        )
    }

    if (isExtension) {
        return intl.formatMessage(
            {
                id: 'components_task_dialog_grant_task_extension',
                defaultMessage: 'Extension: {subject} needs {entitlement} in {app}',
            },
            {
                subject: userName,
                entitlement: task.appEntitlements?.[0]?.displayName,
                app: task.app?.displayName,
            },
        )
    }

    return intl.formatMessage(
        {
            id: 'components_task_dialog_grant_task',
            defaultMessage: '{subject} needs {entitlement} in {app}',
        },
        {
            subject: userName,
            entitlement: task.appEntitlements?.[0]?.displayName,
            app: task.app?.displayName,
        },
    )
}

const getRevokeTaskTitleIntl = (task: TaskValue, intl: IntlShape): string => {
    const userName =
        task.user?.displayName || task.identityUser?.displayName || task.appUser?.displayName

    if (task.appEntitlements?.[0]?.id === SystemAnyAccessEntitlementID) {
        return intl.formatMessage(
            {
                id: 'components_task_dialog_revoke_task_app_access',
                defaultMessage: 'Revoke {subject} access to {app}',
            },
            {
                subject: userName,
                app: task.app?.displayName,
            },
        )
    }

    return intl.formatMessage(
        {
            id: 'components_task_dialog_revoke_task',
            defaultMessage: 'Revoke {subject} access to {entitlement} in {app}',
        },
        {
            subject: userName,
            entitlement: task.appEntitlements?.[0]?.displayName,
            app: task.app?.displayName,
        },
    )
}

const getCertifyTaskTitleIntl = (task: TaskValue, intl: IntlShape): string => {
    const userName = task.user?.displayName || task.identityUser?.displayName || task.appUser?.displayName

    if (task.appEntitlements?.[0]?.id === SystemAnyAccessEntitlementID) {
        return intl.formatMessage(
            {
                id: 'components_task_dialog_certify_task_app_access',
                defaultMessage: 'Review {subject} access to {app}',
            },
            {
                subject: userName,
                app: task.app?.displayName,
            },
        )
    }

    return intl.formatMessage(
        {
            id: 'components_task_dialog_certify_task',
            defaultMessage: 'Review {subject} access to {entitlement} in {app}',
        },
        {
            subject: userName,
            entitlement: task.appEntitlements?.[0]?.displayName,
            app: task.app?.displayName,
        },
    )
}

const getActionTaskTitleIntl = (task: TaskValue, intl: IntlShape): string => {
    const userName = task.user?.displayName || task.identityUser?.displayName || task.appUser?.displayName

    if (userName) {
        return intl.formatMessage(
            {
                id: 'components_task_dialog_action_task',
                defaultMessage: 'Action task for {subject}',
            },
            {
                subject: userName,
            },
        )
    }

    return intl.formatMessage({
        id: 'components_task_dialog_action_task_no_subject',
        defaultMessage: 'Action task',
    })
}
